/**
 * Phase 5: Optimized React Query configuration
 * Reduces refetch frequency and extends cache times for better performance
 */
export const queryConfig = {
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - balance freshness & performance
      gcTime: 10 * 60 * 1000, // 10 minutes - extended cache time
      retry: 1, // Faster failure detection
      refetchOnWindowFocus: false, // Disable aggressive refetching
      refetchOnReconnect: false, // Rely on stale time instead
      structuralSharing: true, // Optimize re-renders
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
};

// Query key factory for consistent cache management
export const queryKeys = {
  // Authentication
  auth: {
    session: ['auth', 'session'] as const,
    user: (userId: string) => ['auth', 'user', userId] as const,
  },
  
  // Organizations
  organizations: {
    all: ['organizations'] as const,
    lists: () => ['organizations', 'list'] as const,
    details: () => ['organizations', 'detail'] as const,
    detail: (id: string) => ['organizations', id] as const,
    byUser: (userId: string) => ['organizations', 'user', userId] as const,
    subscription: (id: string) => ['organizations', 'subscription', id] as const,
    usageStats: (id: string, days: number) => ['organizations', 'usage', id, days] as const,
  },
  
  // Posts
  posts: {
    all: ['posts'] as const,
    lists: () => ['posts', 'list'] as const,
    list: (filters: Record<string, unknown>) => ['posts', 'list', filters] as const,
    details: () => ['posts', 'detail'] as const,
    detail: (id: string) => ['posts', 'detail', id] as const,
    scheduled: (orgId: string) => ['posts', 'scheduled', orgId] as const,
    analytics: (postId: string) => ['posts', 'analytics', postId] as const,
    byMonth: (organizationId: string, year: number, month: number) => ['posts', 'by-month', organizationId, year, month] as const,
  },
  
  // Campaigns
  campaigns: {
    all: ['campaigns'] as const,
    lists: () => ['campaigns', 'list'] as const,
    list: (orgId: string) => ['campaigns', 'list', orgId] as const,
    details: () => ['campaigns', 'detail'] as const,
    detail: (id: string) => ['campaigns', 'detail', id] as const,
    templates: () => ['campaigns', 'templates'] as const,
    analytics: (id: string) => ['campaigns', 'analytics', id] as const,
  },
  
  // Analytics
  analytics: {
    posts: (orgId: string) => ['analytics', 'posts', orgId] as const,
    campaigns: (orgId: string) => ['analytics', 'campaigns', orgId] as const,
    links: (linkId: string) => ['analytics', 'links', linkId] as const,
    summary: (orgId: string, startDate?: string, endDate?: string) => 
      ['analytics', 'summary', orgId, startDate, endDate] as const,
    linkAnalytics: (linkId: string) => ['analytics', 'link', linkId] as const,
    bestTimes: (orgId: string) => ['analytics', 'best-times', orgId] as const,
    insights: (orgId: string) => ['analytics', 'insights', orgId] as const,
  },
  
  // Profiles
  profiles: {
    all: ['profiles'] as const,
    detail: (userId: string) => ['profiles', userId] as const,
    organization: (userId: string) => ['profiles', 'org', userId] as const,
    role: (userId: string, orgId: string) => ['profiles', 'role', userId, orgId] as const,
  },
  
  // Notifications
  notifications: {
    all: (userId: string) => ['notifications', userId] as const,
    unread: (userId: string) => ['notifications', userId, 'unread'] as const,
    preferences: (userId: string) => ['notifications', 'preferences', userId] as const,
  },
  
  // Short Links
  links: {
    all: ['links'] as const,
    list: (orgId: string) => ['links', 'list', orgId] as const,
    detail: (id: string) => ['links', 'detail', id] as const,
    analytics: (linkId: string) => ['links', 'analytics', linkId] as const,
  },
  
  // Referrals
  referrals: {
    all: ['referrals'] as const,
    byUser: (userId: string) => ['referrals', 'user', userId] as const,
    code: (userId: string) => ['referrals', 'code', userId] as const,
    stats: (userId: string) => ['referrals', 'stats', userId] as const,
    rewards: (userId: string) => ['referrals', 'rewards', userId] as const,
  },
  
  // Usage
  usage: {
    byOrg: (orgId: string) => ['usage', orgId] as const,
  },
  
  // Admin
  admin: {
    users: ['admin', 'users'] as const,
    alerts: ['admin', 'alerts'] as const,
    featureFlags: ['admin', 'feature-flags'] as const,
  },
} as const;
